import { PauseErrorCode, PauseOutcome, SubscriptionPause } from '../types/subscription-pause.types';
import { PAUSE_MONTH_DAYS, STALE_PAUSE_CODES, addDays, toDate } from './subscription-pause.util';

/**
 * Staff-facing text for membership pause (CWS-1469). Pure string building over domain data, so
 * the Customers tab and the lane popups word everything the same way.
 */

/** "Nov 18, 2026" */
export function formatPauseDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Tag text for a membership card's status (`cardStatus`). */
export function subscriptionStatusLabel(status: string | null | undefined): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'cancelling':
      return 'Cancelling';
    case 'cancelled':
      return 'Cancelled';
    case 'paused':
      return 'Paused';
    case 'scheduled_pause':
      return 'Pause scheduled';
    default:
      return 'Expired';
  }
}

const REASON_COPY: Record<PauseErrorCode, string> = {
  PAUSE_DISABLED: 'Pausing memberships is not available yet',
  FAMILY_PLAN: "Family plans can't be paused",
  NOT_ELIGIBLE: 'Only memberships billed through Way can be paused',
  NOT_ACTIVE: 'Only active memberships can be paused',
  CANCEL_PENDING: "This membership is cancelling at period end and can't be paused",
  ALREADY_PAUSED: 'This membership already has a pause',
  BILLING_RESTART_PENDING: 'This membership can be paused again once billing restarts',
  PLAN_CHANGE_PENDING: 'Finish or cancel the pending plan change before pausing this membership',
  INVALID_DATES: 'Choose a pause start date',
  START_IN_PAST: "The pause can't start in the past. Choose today or a later date.",
  TOO_SHORT: 'Pause must be at least 1 week',
  TOO_LONG: "Pause can't be longer than 4 months",
  SUBSCRIPTION_NOT_FOUND: 'This membership could not be found. Refresh and try again.',
  PAUSE_UNSUPPORTED_SCHEDULE:
    "This membership has a scheduled plan or discount change and can't be paused",
  NO_PAYMENT_METHOD: 'Add a payment method to this membership before pausing it',
  BILLING_UPDATE_FAILED: 'Billing could not be updated. Nothing was changed. Try again.',
  NOT_SCHEDULED: 'This membership has no scheduled pause to cancel',
  NOT_PAUSED: 'This membership is not paused',
  FORBIDDEN: "You don't have permission to change this membership",
  UNKNOWN: 'Something went wrong. Try again.',
};

/** Why Pause is unavailable, or why a pause action was refused. */
export function pauseReasonCopy(code: PauseErrorCode | null | undefined): string {
  return code ? (REASON_COPY[code] ?? REASON_COPY.UNKNOWN) : '';
}

/** Shown when the membership changed after the card loaded (another terminal, the lane, the app). */
export const PAUSE_STALE_COPY = 'Membership changed, refresh';

/** Text for a refused pause action: stale-state refusals read as a refresh prompt. */
export function pauseRefusalCopy(code: PauseErrorCode | null | undefined): string {
  return code && STALE_PAUSE_CODES.has(code) ? PAUSE_STALE_COPY : pauseReasonCopy(code);
}

function formatMonthlyPrice(price: string | number | null): string {
  const amount = Number(price);
  if (price === null || price === '' || !Number.isFinite(amount)) {
    return '';
  }
  return `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)}/mo`;
}

/** The last paused day: the day before access resumes. */
function pausedUntil(pause: SubscriptionPause): Date | null {
  const resume = toDate(pause.resume_date);
  return resume ? addDays(resume, -1) : null;
}

/** "billing restarts on Nov 19, 2026 at $37.99/mo." Empty without a restart date. */
function billingRestartCopy(pause: SubscriptionPause): string {
  const restart = toDate(pause.billing_restarts_on);
  if (!restart) {
    return '';
  }
  const price = formatMonthlyPrice(pause.plan_price);
  const at = price ? ' at ' + price : '';
  return `billing restarts on ${formatPauseDate(restart)}${at}.`;
}

/** Banner under a paused or scheduled card. Empty when the pause dates are missing. */
export function pauseBannerCopy(pause: SubscriptionPause | null): string {
  const until = pause ? pausedUntil(pause) : null;
  if (!pause || !until) {
    return '';
  }
  const billing = billingRestartCopy(pause);

  if (pause.state === 'paused') {
    const tail = billing ? ' – ' + billing : '.';
    return `Membership paused until ${formatPauseDate(until)}. Activate now to redeem today${tail}`;
  }
  if (pause.state === 'scheduled') {
    const start = toDate(pause.start);
    const from = start ? ' from ' + formatPauseDate(start) : '';
    const tail = billing ? ' B' + billing.slice(1) : '';
    return `Pause scheduled${from} until ${formatPauseDate(until)}.${tail}`;
  }
  return '';
}

export interface PauseOutcomeCopy {
  title: string;
  message: string;
}

/** Result modal text after a pause action succeeds. */
export function pauseOutcomeCopy(outcome: PauseOutcome): PauseOutcomeCopy {
  const plate = outcome.plate || 'this vehicle';
  switch (outcome.kind) {
    case 'paused':
      return {
        title: 'Plan paused successfully',
        message: `Membership for ${plate} is paused until ${formatPauseDate(addDays(outcome.resumeDate, -1))}.`,
      };
    case 'scheduled':
      return {
        title: 'Pause scheduled',
        message: `Membership for ${plate} will pause from ${formatPauseDate(outcome.start)} until ${formatPauseDate(addDays(outcome.resumeDate, -1))}.`,
      };
    case 'activated':
      return {
        title: 'Plan activated successfully',
        message: `Membership for ${plate} is active again. Nothing was charged today.`,
      };
    case 'scheduled_cancelled':
      return {
        title: 'Scheduled pause cancelled',
        message: `Membership for ${plate} will stay active.`,
      };
  }
}

/** Length as a whole number of 30-day months when it is one, otherwise in days. */
export function pauseLengthLabel(days: number): string {
  if (days > 0 && days % PAUSE_MONTH_DAYS === 0) {
    const months = days / PAUSE_MONTH_DAYS;
    return `${months} month${months === 1 ? '' : 's'}`;
  }
  return `${days} day${days === 1 ? '' : 's'}`;
}

/** "Pause for 1 month" / "Pause for 10 days" */
export function pauseConfirmLabel(days: number): string {
  return `Pause for ${pauseLengthLabel(days)}`;
}

/** Activate now confirmation: what the customer is charged and when billing resumes. */
export function activateConfirmCopy(
  bankedDays: number | null | undefined,
  today: Date = new Date()
): string {
  if (!bankedDays || bankedDays <= 0) {
    return 'Activating now charges $0 today.';
  }
  return `$0 today · next charge ${formatPauseDate(addDays(today, bankedDays))}`;
}
